<template>
  <div id="list-customers" class="container-fluid">
    <!-- Customer Filters -->
    <b-form
      id="customers-form"
      name="customersForm"
      class="pt-1 page-form"
      autocomplete="off">
      <input autocomplete="false" name="hidden" type="text" style="display:none;">
      <div class="row">
        <!-- Filters -->
        <div
          id="customer-filters"
          class="col page-filters">
          <div class="row">
            <!-- General Text Filter -->
            <div class="col-3 pr-0">
              <label for="filter-text">
                Filter Customers:
                <font-awesome-icon
                  icon="info-circle"
                  v-b-tooltip="{
                    title:'Filters customers by Name, Company, Email and Phone.',
                    placement:'bottom',
                    delay:{'show':500}
                  }">
                </font-awesome-icon>
              </label>

              <b-input-group size="sm">
                <b-form-input
                  id="filter-text"
                  type="text"
                  name="filterText"
                  placeholder="Type any filter text"
                  v-model="filters.text"
                />
                <b-input-group-append>
                  <b-button
                    :disabled="!filters.text"
                    @click="filters.text = '';">
                    <font-awesome-icon icon="times"></font-awesome-icon>
                  </b-button>
                </b-input-group-append>
              </b-input-group>
            </div>
            <!-- / General Text Filter -->

            <!-- Over Activations -->
            <div class="col-3 pr-0">
              <label>&nbsp;</label><br>
              <b-form-checkbox
                class="d-flex align-items-center h-31"
                v-model="filters.overActivations">
                Customers over activation limit
              </b-form-checkbox>
            </div>
            <!-- Over Activations -->
          </div>
        </div>
        <!-- / Filters -->

        <!-- Actions -->
        <div
          id="customer-actions"
          class="col-auto d-flex align-items-end page-actions">
          <b-dropdown
            right
            size="sm"
            text="Actions">
            <b-dropdown-item
              class="p-2"
              @click="goToCreateLicenses();">
              <font-awesome-icon
                icon="id-card"
                class="mr-1">
              </font-awesome-icon>
              Create License(s)
            </b-dropdown-item>

            <b-dropdown-item
              class="p-2"
              @click="viewCustomer({ ID: 'new' })">
              <font-awesome-icon
                icon="plus-circle"
                class="mr-1">
              </font-awesome-icon>
              Create New Customer
            </b-dropdown-item>

            <b-dropdown-item
              class="p-2"
              :disabled="selectedCustomerIDs.length !== 1"
              @click="viewCustomer({ ID: selectedCustomerIDs[0] })">
              <font-awesome-icon
                icon="pen"
                class="mr-1">
              </font-awesome-icon>
              Edit Customer
            </b-dropdown-item>
          </b-dropdown>
        </div>
        <!-- / Actions -->
      </div>
    </b-form>
    <!-- / Customer Filters -->

    <!-- Customer List -->
    <div
      id="customer-results"
      class="results-list">
      <b-table
        class="table-fixed"
        v-table-lines
        striped
        hover
        selectable
        select-mode="range"
        @row-dblclicked="viewCustomer"
        @row-selected="selectCustomers"
        :sort-compare="sort"
        :items="filteredCustomers"
        :fields="customerFields"
        :show-empty="true"
        empty-text="There are no customers to show."
        empty-filtered-text="There are no customers to show.">
        <!-- Custom Name -->
        <template slot="LastName" slot-scope="data">
          {{data.item.LastName}}, {{data.item.FirstName}}
        </template>
        <!-- / Custom Name -->

        <!-- Custom Activations -->
        <template
          slot="NumberOfActivations"
          slot-scope="data">
          <span
            :class="{
              'text-danger':data.item.NumberOfActivations > data.item.NumberOfLicenses
            }">
            {{data.item.NumberOfActivations}} / {{data.item.NumberOfLicenses}}
          </span>
        </template>
        <!-- / Custom Activations -->

        <!-- Custom Expiration -->
        <template
          slot="ValidThru"
          slot-scope="data">
          <span
            :class="{
              'text-danger':data.item.ValidThru.isBefore(),
            }">
            {{data.item.ValidThru.format('M/D/YYYY')}}
          </span>
        </template>
        <!-- / Custom Expiration -->
      </b-table>
    </div>
  </div>
</template>

<script>
import _ from 'lodash';
import DataSvc from '../services/data';
import mixins from '../services/mixins';
import { EventBus, GlobalLoaderMixin } from '../services/event-bus';

export default {
  name: 'list-customers',
  mixins: [
    GlobalLoaderMixin,
    mixins,
  ],
  data() {
    return {
      // filters form
      filters: {
        overActivations: false,
      },

      // customer list
      customers: [],
      customerFields: [
        {
          label: 'Name',
          key: 'LastName',
          sortable: true,
          class: 'customer-name ellipsis',
        },
        {
          label: 'Company',
          key: 'Company',
          sortable: true,
          class: 'customer-company ellipsis',
        },
        {
          label: 'Email',
          key: 'Email',
          sortable: true,
          class: 'customer-email ellipsis',
        },
        {
          label: 'Phone',
          key: 'Phone',
          sortable: true,
          class: 'customer-phone ellipsis',
          tdClass: 'text-right',
        },
        {
          label: 'Activations',
          key: 'NumberOfActivations',
          sortable: true,
          class: 'customer-activations ellipsis',
          tdClass: 'text-right',
        },
      ],
      selectedCustomerIDs: [],
    };
  },
  computed: {
    filteredCustomers() {
      const text = c => c.FirstName.toLowerCase().includes(this.filters.text)
        || c.LastName.toLowerCase().includes(this.filters.text)
        || (c.Company && c.Company.toLowerCase().includes(this.filters.text))
        || (c.Email && c.Email.toLowerCase().includes(this.filters.text))
        || (c.Phone && c.Phone.toLowerCase().includes(this.filters.text))
        || !this.filters.text;

      const activation = c => (this.filters.overActivations
          && c.NumberOfActivations > c.NumberOfLicenses)
        || !this.filters.overActivations;

      return _.chain(this.customers)
        .filter(text)
        .filter(activation)
        .value();
    },
  },
  methods: {
    getCustomers() {
      return DataSvc.getCustomers()
        .then((customers) => {
          this.customers = customers;
          this.$nextTick(() => {
            EventBus.$emit('table-data-loaded');
          });
        });
    },
    viewCustomer(customer) {
      this.$router.push({ name: 'view-customer', params: customer });
    },
    selectCustomers(customers) {
      this.selectedCustomerIDs = _.map(customers, 'ID');
    },
    goToCreateLicenses() {
      const params = {};

      if (this.selectedCustomerIDs.length === 1) {
        [params.ID] = this.selectedCustomerIDs;
      }

      this.$router.push({ name: 'new-license', params });
    },
  },
  created() {
    this.showGlobalLoader('Loading customers...');
    this.getCustomers()
      .finally(() => {
        this.hideGlobalLoader();
      });
  },
};
</script>

<style lang="scss">
#customer-results {
  height: calc(100% - 59px - 2rem);
  margin: 1rem 0;

  table.table-fixed {
    th,
    td {
      &.customer-name {
        width: calc((100% - 2 * 150px) / 3);
      }

      &.customer-company {
        width: calc((100% - 2 * 150px) / 3);
      }

      &.customer-email {
        width: calc((100% - 2 * 150px) / 3);
      }

      &.customer-phone {
        width: 150px;
      }

      &.customer-activations {
        width: 150px;
      }
    }
  }
}
</style>
